import { CylinderGeometry, Mesh, MeshStandardMaterial, Quaternion, Vector3 } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugCylinderConfig } from '../types';

export interface AddCylinderParams {
	config: DebugCylinderConfig;
}

/**
 * Add a debug cylinder (registered as a sphere for tracking/cleanup)
 */
export const addCylinderCfn = (
	self: DebugManager,
	{ config }: AddCylinderParams
): Mesh | null => {
	const { id, position, radius = 1, height = 4, color = 0xff8800, label, axis } = config;

	if (!radius || radius <= 0 || !Number.isFinite(radius)) {
		return null;
	}

	if (!Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
		return null;
	}

	// Remove existing mesh with same ID
	if (self.hasSphere(id)) {
		self.removeSphere(id);
	}

	const geometry = new CylinderGeometry(radius, radius, height, 16);
	const material = new MeshStandardMaterial({ color });
	const cylinder = new Mesh(geometry, material);
	cylinder.position.copy(position);

	// Orient cylinder along axis if provided (default is Y-up)
	if (axis) {
		const defaultAxis = new Vector3(0, 1, 0);
		const normalizedAxis = axis.clone().normalize();
		const quaternion = new Quaternion().setFromUnitVectors(defaultAxis, normalizedAxis);
		cylinder.quaternion.copy(quaternion);
	}

	cylinder.name = `debug_cylinder_${id}`;
	cylinder.visible = self.visible;
	cylinder.userData.isInterface = true;
	cylinder.userData.type = 'debugVisualization';

	self.scene.add(cylinder);
	self.registerSphere(id, cylinder);

	if (label) {
		self.registerSphereLabel(id, label);
		self.createTooltip(id);
	}

	if (self.visible && !self.hoverEnabled) {
		self.enableHoverDetection();
	}

	return cylinder;
};
